package fr.manyverse.app.presentation.dice3d

import fr.manyverse.app.domain.model.DieType
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.acos
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.roundToInt
import kotlin.math.sin
import kotlin.math.sqrt
import kotlin.math.tan

/**
 * Face plane d'un polyèdre convexe. Les faces opposées somment à `sides + 1` (convention des
 * dés physiques).
 *
 * [value] est nul sur les faces qui ne portent pas de chiffre : les coiffes pyramidales du d4
 * barillet n'existent que pour l'empêcher de rester debout sur une pointe. Voir
 * [PolyhedronMesh.valueFaces].
 */
class Face(
    val indices: List<Int>,
    val normal: Vec3,
    val centroid: Vec3,
    /** Direction « haut du texte » dans l'espace modèle, perpendiculaire à la normale. */
    val textUp: Vec3,
    /** Complément de [textUp] dans le plan de la face (normal × textUp), précalculé pour le rendu. */
    val textRight: Vec3,
    /** Distance minimale centroïde → arête, sert à dimensionner le glyphe. */
    val inradius: Float,
    val value: Int?
)

class PolyhedronMesh(
    val vertices: List<Vec3>,
    val faces: List<Face>,
    /**
     * Rayon qu'aurait une bille roulant à la même allure que ce solide, en multiples du
     * circumradius. Une culbute fait passer le dé d'une face à sa voisine : il tourne de
     * l'angle entre les deux normales pendant que son centre avance d'une distance fixée
     * par la géométrie, et le rapport des deux est ce rayon. Il vaut 0,57 pour le d4 et
     * 0,89 pour le d12 : un dé n'est d'autant plus proche d'une bille qu'il a de faces.
     */
    val rollingRadius: Float,
    /**
     * Surcroît de hauteur à franchir pour basculer d'une face vers sa voisine, en multiples
     * du circumradius : le centre doit passer par l'aplomb de l'arête, plus haut que
     * l'aplomb de la face. C'est la **barrière d'énergie d'une culbute**, et elle sépare
     * radicalement les dés — 0,18 pour un d4, 0,056 pour un d20 presque rond. En dessous de
     * la vitesse qui permet de la franchir, un dé posé ne peut plus que rester où il est.
     */
    val tumbleBarrier: Float,
    /**
     * Moments d'inertie principaux du solide plein, pour une masse de 1 et un circumradius de
     * 1 — l'inertie réelle sur le plateau vaut donc `inertia × dieRadius²`.
     *
     * Diagonaux dans le repère du maillage : ces solides sont assez symétriques pour que leurs
     * axes de symétrie soient leurs axes principaux (vérifié à la construction). Les solides de
     * Platon sont isotropes, si bien que leur rotation libre est une rotation uniforme ; le
     * barillet du d4 et le trapézoèdre du d10 ne le sont pas, et **précessent** en l'air comme
     * un vrai dé cristal qui se dandine autour de son axe long.
     */
    val inertia: Vec3
) {
    /**
     * Faces qui portent un chiffre — celles sur lesquelles le dé a le droit de se coucher.
     * Identique à [faces] pour tous les solides sauf le d4 barillet, dont les coiffes n'en
     * font pas partie : c'est ce qui empêche le simulateur de le laisser debout sur une pointe.
     */
    val valueFaces: List<Face> = faces.filter { it.value != null }

    fun faceForValue(value: Int): Face = faces.first { it.value == value }

    /**
     * Hauteur du centre quand le solide, tourné par [orientation], repose sur le feutre —
     * en multiples du circumradius. C'est la fonction d'appui du polyèdre : posé à plat sur
     * une face le centre descend à l'inradius, dressé sur une arête il remonte au midradius.
     * Ce relief est ce qui fait culbuter un dé au lieu de le faire rouler comme une bille.
     */
    fun supportHeight(orientation: Quaternion): Float {
        var lowest = 0f
        // Boucle indexée : appelée pour chaque dé à chaque pas, un itérateur s'y paierait.
        for (index in vertices.indices) {
            val depth = -orientation.rotatedZ(vertices[index])
            if (depth > lowest) lowest = depth
        }
        return lowest
    }

    /**
     * Appui du solide sur le feutre : [supportHeight], **plus les sommets qui touchent
     * réellement**. C'est là que se joue la différence entre un dé et une bille — un point
     * d'appui à l'aplomb du centre ne peut ni faire pivoter le dé sur son arête, ni le faire
     * dévier, alors qu'un appui décalé le fait basculer sous son propre poids.
     *
     * Un seul sommet retenu : le dé est sur une pointe. Deux : sur une arête. Trois ou plus :
     * couché sur une face. Le résultat est déposé dans [into], réutilisé d'un pas à l'autre.
     */
    fun support(orientation: Quaternion, into: SupportContact) {
        var lowest = 0f
        for (index in vertices.indices) {
            val depth = -orientation.rotatedZ(vertices[index])
            if (depth > lowest) lowest = depth
        }
        val threshold = lowest - SupportContact.TOLERANCE
        var count = 0
        for (index in vertices.indices) {
            if (count >= SupportContact.MAX_POINTS) break
            if (-orientation.rotatedZ(vertices[index]) >= threshold) {
                into.vertices[count] = index
                count++
            }
        }
        into.height = lowest
        into.count = count
    }

    /**
     * Étendue du solide tourné par [orientation] dans la direction unitaire [outward], en
     * multiples du circumradius : la distance du centre au plan qui le borne de ce côté.
     *
     * C'est [supportHeight] pour une direction quelconque. Elle vaut l'inradius quand une face
     * regarde dans cette direction et 1 quand c'est une pointe — un dé n'est **pas** une
     * sphère, et un cube posé à plat contre un mur en est à 0,58 rayon, pas à 1.
     */
    fun reach(orientation: Quaternion, outward: Vec3): Float {
        // La direction est ramenée une fois dans le repère du dé : chaque sommet ne coûte plus
        // qu'un produit scalaire, sans rien allouer.
        val local = orientation.inverseRotate(outward)
        var farthest = -Float.MAX_VALUE
        for (index in vertices.indices) {
            val extent = vertices[index] dot local
            if (extent > farthest) farthest = extent
        }
        return farthest
    }

    /**
     * Appui du solide sur un plan qui le borne dans la direction [outward] : [reach], **plus
     * les sommets qui touchent ce plan** — un pour une pointe, deux pour une arête, trois ou
     * plus pour une face. C'est [support] pour une paroi, ou pour un dé voisin.
     */
    fun support(orientation: Quaternion, outward: Vec3, into: SupportContact) {
        val local = orientation.inverseRotate(outward)
        var farthest = -Float.MAX_VALUE
        for (index in vertices.indices) {
            val extent = vertices[index] dot local
            if (extent > farthest) farthest = extent
        }
        val threshold = farthest - SupportContact.TOLERANCE
        var count = 0
        for (index in vertices.indices) {
            if (count >= SupportContact.MAX_POINTS) break
            if ((vertices[index] dot local) >= threshold) {
                into.vertices[count] = index
                count++
            }
        }
        into.height = farthest
        into.count = count
    }
}

/**
 * Sommets par lesquels un dé touche le feutre, et hauteur de son centre à cet instant.
 * Tampon réutilisé : la simulation le remplit pour chaque dé à chaque pas.
 */
class SupportContact {

    /**
     * Distance du centre au plan d'appui, en multiples du circumradius : la hauteur du centre
     * pour le feutre, l'étendue du dé vers la paroi pour un mur.
     */
    var height: Float = 0f
        internal set

    /** Indices des sommets d'appui dans [PolyhedronMesh.vertices], remplis jusqu'à [count]. */
    val vertices: IntArray = IntArray(MAX_POINTS)

    var count: Int = 0
        internal set

    companion object {
        /**
         * Bande d'appui, en multiples du circumradius. Un sommet à peine plus haut que le plus
         * bas porte quand même : sans cette tolérance, un dé couché à plat n'aurait qu'un seul
         * point de contact — celui que l'arrondi du calcul aurait fait sortir le premier — et
         * tomberait indéfiniment d'un coin sur l'autre.
         */
        const val TOLERANCE = 1e-3f

        /** La plus grande face est le pentagone du d12 ; la marge couvre les cas limites. */
        const val MAX_POINTS = 8
    }
}

/**
 * Maillages des dés, générés par extraction des faces de l'enveloppe convexe d'un nuage
 * de sommets (circumradius normalisé à 1). Mémoïsés : la construction O(n⁴) ne tourne
 * qu'une fois par type.
 */
object DiceMeshes {

    private val PHI = ((1.0 + sqrt(5.0)) / 2.0).toFloat()
    private const val PLANE_EPSILON = 1e-3f

    /** Axe long du d4 barillet, et pointe de ses coiffes : le circumradius y vaut 1. */
    private val BARREL_AXIS = Vec3(0f, 0f, 1f)

    /** Demi-largeur et demi-longueur du corps du d4 barillet — sa silhouette tient dans ces deux
     * nombres. Ils donnent un rapport longueur/largeur de 2,3 et une hauteur de coiffe de
     * `1 − BARREL_HALF_LENGTH`. Toute retouche doit conserver
     * `(1 − BARREL_HALF_LENGTH) × BARREL_HALF_LENGTH > BARREL_HALF_WIDTH²`, sans quoi le dé
     * redevient stable sur une pointe. */
    private const val BARREL_HALF_WIDTH = 0.44f
    private const val BARREL_HALF_LENGTH = 0.58f

    private val cache = mutableMapOf<DieType, PolyhedronMesh>()

    fun meshFor(type: DieType): PolyhedronMesh = cache.getOrPut(type) {
        val vertices = verticesFor(type)
        buildMesh(vertices, sidesFor(type), valueAxisFor(type))
    }

    /** Nombre de faces du solide rendu (le d100 est affiché avec des d10). */
    fun sidesFor(type: DieType): Int = if (type == DieType.D100) 10 else type.sides

    /**
     * Axe long du solide, pour les dés dont toutes les faces ne portent pas de chiffre. Seul le
     * d4 barillet en a un : ses faces à valeur sont celles qui lui sont perpendiculaires, et le
     * haut de leur texte se lit en travers de cet axe. Null partout ailleurs — chaque face de
     * l'enveloppe porte alors une valeur.
     */
    private fun valueAxisFor(type: DieType): Vec3? =
        if (type == DieType.D4) BARREL_AXIS else null

    private fun verticesFor(type: DieType): List<Vec3> = when (type) {
        // Dé cristal : corps prismatique à section carrée coiffé d'une pyramide à chaque bout.
        // Seules les 4 faces latérales portent un chiffre, et c'est celle du dessus qui donne le
        // résultat. Les coiffes ne servent qu'à interdire le repos debout : le pied de la
        // perpendiculaire abaissée du centre sur un de leurs triangles tombe hors du triangle
        // dès que (1 − BARREL_HALF_LENGTH) × BARREL_HALF_LENGTH > BARREL_HALF_WIDTH², ce que ces
        // proportions vérifient avec 26 % de marge. Les pointes sont à distance 1 : circumradius,
        // comme pour tous les autres maillages.
        DieType.D4 -> buildList {
            for (sx in listOf(-1f, 1f)) for (sy in listOf(-1f, 1f)) for (sz in listOf(-1f, 1f)) {
                add(Vec3(sx * BARREL_HALF_WIDTH, sy * BARREL_HALF_WIDTH, sz * BARREL_HALF_LENGTH))
            }
            add(BARREL_AXIS)
            add(-BARREL_AXIS)
        }

        DieType.D6 -> cubeCorners().map { it.normalized() }

        DieType.D8 -> listOf(
            Vec3(1f, 0f, 0f), Vec3(-1f, 0f, 0f),
            Vec3(0f, 1f, 0f), Vec3(0f, -1f, 0f),
            Vec3(0f, 0f, 1f), Vec3(0f, 0f, -1f)
        )

        DieType.D10, DieType.D100 -> trapezohedronVertices()

        DieType.D12 -> {
            val inv = 1f / PHI
            val ring = mutableListOf<Vec3>()
            for (s1 in listOf(-1f, 1f)) for (s2 in listOf(-1f, 1f)) {
                ring += Vec3(0f, s1 * inv, s2 * PHI)
                ring += Vec3(s2 * PHI, 0f, s1 * inv)
                ring += Vec3(s1 * inv, s2 * PHI, 0f)
            }
            (cubeCorners() + ring).map { it.normalized() }
        }

        DieType.D20 -> {
            val verts = mutableListOf<Vec3>()
            for (s1 in listOf(-1f, 1f)) for (s2 in listOf(-1f, 1f)) {
                verts += Vec3(0f, s1 * 1f, s2 * PHI)
                verts += Vec3(s2 * PHI, 0f, s1 * 1f)
                verts += Vec3(s1 * 1f, s2 * PHI, 0f)
            }
            verts.map { it.normalized() }
        }
    }

    private fun cubeCorners(): List<Vec3> = buildList {
        for (sx in listOf(-1f, 1f)) for (sy in listOf(-1f, 1f)) for (sz in listOf(-1f, 1f)) {
            add(Vec3(sx, sy, sz))
        }
    }

    /**
     * Trapézoèdre pentagonal (d10) : 2 apex + anneau équatorial en zigzag de 10 sommets.
     * La planéité des cerfs-volants impose h = c·tan²(18°).
     */
    private fun trapezohedronVertices(): List<Vec3> {
        val h = tan(PI / 10.0).let { it * it }.toFloat()
        val verts = mutableListOf(Vec3(0f, 0f, 1f), Vec3(0f, 0f, -1f))
        for (k in 0 until 10) {
            val angle = (k * PI / 5.0)
            val z = if (k % 2 == 0) h else -h
            verts += Vec3(cos(angle).toFloat(), sin(angle).toFloat(), z)
        }
        val maxLen = verts.maxOf { it.length }
        return verts.map { it * (1f / maxLen) }
    }

    /**
     * Construit le maillage à partir du nuage de sommets. [valueAxis], s'il est fourni, désigne
     * l'axe long du solide : seules les faces qui lui sont perpendiculaires portent alors un
     * chiffre, les autres n'appartiennent qu'à l'enveloppe (voir [PolyhedronMesh.valueFaces]).
     */
    private fun buildMesh(
        vertices: List<Vec3>,
        expectedSides: Int,
        valueAxis: Vec3?
    ): PolyhedronMesh {
        val planes = extractSupportingPlanes(vertices)
        val ordered = planes.sortedWith(
            compareBy({ (it.normal.z * 1000).roundToInt() }, { (it.normal.y * 1000).roundToInt() }, { (it.normal.x * 1000).roundToInt() })
        )
        val carriesValue = ordered.map { valueAxis == null || abs(it.normal dot valueAxis) < PLANE_EPSILON }
        require(carriesValue.count { it } == expectedSides) {
            "Extraction de faces incohérente : ${carriesValue.count { it }} faces à valeur " +
                "trouvées sur ${ordered.size} faces d'enveloppe, $expectedSides attendues"
        }
        val values = assignValues(ordered.map { it.normal }, carriesValue, expectedSides)
        val faces = ordered.mapIndexed { index, plane ->
            val faceVerts = plane.indices.map { vertices[it] }
            val centroid = faceVerts.reduce(Vec3::plus) * (1f / faceVerts.size)
            val textUp = textUpFor(plane.normal, valueAxis.takeIf { carriesValue[index] })
            Face(
                indices = plane.indices,
                normal = plane.normal,
                centroid = centroid,
                textUp = textUp,
                textRight = plane.normal cross textUp,
                inradius = inradiusOf(centroid, faceVerts),
                value = values[index]
            )
        }
        // Le roulement se caractérise sur les seules faces à valeur : ce sont elles qui décident
        // du résultat, et pour le barillet ça isole ses quatre arêtes longues — celles autour
        // desquelles il roule vraiment — au lieu de noyer la moyenne dans les arêtes de coiffe.
        val valueFaces = faces.filter { it.value != null }
        return PolyhedronMesh(
            vertices = vertices,
            faces = faces,
            rollingRadius = rollingRadiusOf(vertices, valueFaces),
            tumbleBarrier = tumbleBarrierOf(vertices, valueFaces),
            inertia = inertiaOf(vertices, faces)
        )
    }

    /**
     * Moments d'inertie principaux du solide plein de masse 1 (voir [PolyhedronMesh.inertia]).
     *
     * Le volume est découpé en tétraèdres reliant le centre à chaque triangle des faces. Pour
     * un tétraèdre dont un sommet est à l'origine et les trois autres en a, b, c, la matrice
     * des moments seconds vaut `det(A)/120 · A·M·Aᵀ` avec `A = [a b c]` et `M` la matrice qui
     * vaut 2 sur sa diagonale et 1 ailleurs. Le tenseur d'inertie s'en déduit par
     * `I = tr(C)·Id − C`, et la division par le volume total ramène la masse à 1.
     */
    private fun inertiaOf(vertices: List<Vec3>, faces: List<Face>): Vec3 {
        var cxx = 0f; var cyy = 0f; var czz = 0f
        var cxy = 0f; var cxz = 0f; var cyz = 0f
        var volume = 0f
        faces.forEach { face ->
            val corners = face.indices
            // Éventail de triangles depuis le premier sommet : les faces sont convexes et
            // leurs sommets déjà ordonnés autour du centroïde.
            for (i in 1 until corners.size - 1) {
                val a = vertices[corners[0]]
                val b = vertices[corners[i]]
                val c = vertices[corners[i + 1]]
                val det = a dot (b cross c)
                volume += det / 6f
                val scale = det / 120f
                // (A·M·Aᵀ)_ij = 2(aᵢaⱼ + bᵢbⱼ + cᵢcⱼ) + (aᵢbⱼ + bᵢaⱼ) + (aᵢcⱼ + cᵢaⱼ) + (bᵢcⱼ + cᵢbⱼ)
                cxx += scale * moment(a.x, b.x, c.x, a.x, b.x, c.x)
                cyy += scale * moment(a.y, b.y, c.y, a.y, b.y, c.y)
                czz += scale * moment(a.z, b.z, c.z, a.z, b.z, c.z)
                cxy += scale * moment(a.x, b.x, c.x, a.y, b.y, c.y)
                cxz += scale * moment(a.x, b.x, c.x, a.z, b.z, c.z)
                cyz += scale * moment(a.y, b.y, c.y, a.z, b.z, c.z)
            }
        }
        val trace = cxx + cyy + czz
        val ixx = (trace - cxx) / volume
        val iyy = (trace - cyy) / volume
        val izz = (trace - czz) / volume
        // Les axes du maillage doivent être ses axes principaux, faute de quoi une inertie
        // diagonale serait un mensonge et la rotation libre partirait de travers.
        val offDiagonal = maxOf(abs(cxy), abs(cxz), abs(cyz)) / volume
        require(offDiagonal < 1e-3f * maxOf(ixx, iyy, izz)) {
            "Le maillage n'est pas aligné sur ses axes principaux : produit d'inertie résiduel " +
                "$offDiagonal"
        }
        // Et le solide doit être **de révolution** autour de z : la simulation s'appuie sur la
        // solution exacte de la toupie libre axisymétrique pour intégrer la rotation en vol
        // (voir DiceRollSimulation.advanceFreeSpin). C'est le cas de tous ces solides — les
        // polyèdres de Platon sont isotropes, le barillet et le trapézoèdre ont un axe long.
        require(abs(ixx - iyy) < 1e-3f * maxOf(ixx, iyy)) {
            "Le maillage n'est pas de révolution : moments transverses $ixx et $iyy"
        }
        return Vec3(ixx, iyy, izz)
    }

    /** Terme `(A·M·Aᵀ)` pour une paire de coordonnées, voir [inertiaOf]. */
    private fun moment(a1: Float, b1: Float, c1: Float, a2: Float, b2: Float, c2: Float): Float =
        2f * (a1 * a2 + b1 * b2 + c1 * c2) +
            (a1 * b2 + b1 * a2) + (a1 * c2 + c1 * a2) + (b1 * c2 + c1 * b2)

    /**
     * Parcourt les arêtes du solide : deux faces sont voisines lorsqu'elles partagent
     * exactement deux sommets. [action] reçoit les faces et les deux bouts de l'arête.
     */
    private inline fun forEachEdge(
        vertices: List<Vec3>,
        faces: List<Face>,
        action: (a: Face, b: Face, p: Vec3, q: Vec3) -> Unit
    ) {
        for (i in faces.indices) for (j in i + 1 until faces.size) {
            val a = faces[i]
            val b = faces[j]
            val shared = a.indices.filter { it in b.indices }
            if (shared.size != 2) continue
            action(a, b, vertices[shared[0]], vertices[shared[1]])
        }
    }

    /**
     * Rayon de roulement effectif du solide (voir [PolyhedronMesh.rollingRadius]).
     *
     * Pour une culbute par-dessus une arête partagée par deux faces, le dé tourne de
     * l'angle entre leurs normales sortantes, et son centre se déplace horizontalement de
     * la somme des distances de ses deux pieds de perpendiculaire à cette arête. Le rapport
     * des deux donne le rayon.
     *
     * Moyenné sur les arêtes des seules faces à valeur : les solides de Platon n'en ont qu'une
     * sorte, mais le trapézoèdre du d10 en a deux (les longues et les courtes du cerf-volant),
     * et le barillet du d4 roule sur ses quatre arêtes longues, jamais sur ses coiffes.
     */
    private fun rollingRadiusOf(vertices: List<Vec3>, valueFaces: List<Face>): Float {
        var total = 0f
        var edges = 0
        forEachEdge(vertices, valueFaces) { a, b, p, q ->
            val angle = acos((a.normal dot b.normal).coerceIn(-1f, 1f))
            if (angle >= 1e-4f) {
                total += (distanceToLine(footOf(a), p, q) + distanceToLine(footOf(b), p, q)) / angle
                edges++
            }
        }
        return if (edges == 0) 1f else total / edges
    }

    /**
     * Barrière d'énergie d'une culbute (voir [PolyhedronMesh.tumbleBarrier]) : de combien le
     * centre doit monter pour passer de l'aplomb d'une face à celui d'une arête. La hauteur
     * au repos est la distance du centre au plan de la face ; celle du basculement, sa
     * distance à l'arête. Moyennée sur les arêtes des faces à valeur, comme le rayon de roulement.
     */
    private fun tumbleBarrierOf(vertices: List<Vec3>, valueFaces: List<Face>): Float {
        val resting = valueFaces.minOf { it.normal dot it.centroid }
        var total = 0f
        var edges = 0
        forEachEdge(vertices, valueFaces) { _, _, p, q ->
            total += distanceToLine(Vec3.ZERO, p, q) - resting
            edges++
        }
        return if (edges == 0) 0f else (total / edges).coerceAtLeast(0f)
    }

    /** Pied de la perpendiculaire abaissée du centre du solide sur le plan de [face]. */
    private fun footOf(face: Face): Vec3 = face.normal * (face.centroid dot face.normal)

    private fun distanceToLine(point: Vec3, a: Vec3, b: Vec3): Float {
        val edge = b - a
        return ((point - a) cross edge).length / edge.length
    }

    private class SupportingPlane(val normal: Vec3, val indices: List<Int>)

    private fun extractSupportingPlanes(vertices: List<Vec3>): List<SupportingPlane> {
        val found = mutableMapOf<Triple<Int, Int, Int>, SupportingPlane>()
        val n = vertices.size
        for (i in 0 until n) for (j in i + 1 until n) for (k in j + 1 until n) {
            val a = vertices[i]
            var normal = ((vertices[j] - a) cross (vertices[k] - a))
            if (normal.length < 1e-6f) continue
            normal = normal.normalized()
            // Le centroïde du solide est à l'origine : la normale sortante vérifie dot(a, n) > 0.
            if ((a dot normal) < 0f) normal = -normal
            val d = a dot normal
            if (vertices.any { (it dot normal) > d + PLANE_EPSILON }) continue
            val key = Triple(
                (normal.x * 500).roundToInt(), (normal.y * 500).roundToInt(), (normal.z * 500).roundToInt()
            )
            if (key in found) continue
            val coplanar = vertices.indices.filter { abs((vertices[it] dot normal) - d) <= PLANE_EPSILON }
            found[key] = SupportingPlane(normal, orderAroundCentroid(coplanar, vertices, normal))
        }
        return found.values.toList()
    }

    private fun orderAroundCentroid(indices: List<Int>, vertices: List<Vec3>, normal: Vec3): List<Int> {
        val centroid = indices.map { vertices[it] }.reduce(Vec3::plus) * (1f / indices.size)
        val u = (vertices[indices.first()] - centroid).normalized()
        val w = (normal cross u).normalized()
        return indices.sortedBy {
            val rel = vertices[it] - centroid
            atan2(rel dot w, rel dot u)
        }
    }

    /**
     * Attribue les valeurs de sorte que les faces opposées somment à sides+1. Les faces que
     * [carriesValue] écarte n'en reçoivent aucune et restent nulles.
     */
    private fun assignValues(
        normals: List<Vec3>,
        carriesValue: List<Boolean>,
        sides: Int
    ): Array<Int?> {
        val values = arrayOfNulls<Int>(normals.size)
        var next = 1
        for (i in normals.indices) {
            if (!carriesValue[i] || values[i] != null) continue
            values[i] = next
            val opposite = normals.indices.firstOrNull { j ->
                j != i && carriesValue[j] && values[j] == null &&
                    approxEquals(normals[j], -normals[i], 1e-2f)
            }
            if (opposite != null) values[opposite] = sides + 1 - next
            next++
        }
        return values
    }

    /**
     * Direction « haut du texte » dans le plan de la face. Sur un solide à axe long ([valueAxis]
     * non nul), le chiffre se lit **en travers** de l'axe, comme sur un vrai dé cristal : son haut
     * pointe vers un côté long de la face, et [Face.textRight] tombe le long du barillet. Sinon le
     * texte atterrit à un angle quelconque, en projetant +Y dans le plan.
     */
    private fun textUpFor(normal: Vec3, valueAxis: Vec3?): Vec3 {
        if (valueAxis != null) return (normal cross valueAxis).normalized()
        val pick = if (abs(normal dot Vec3(0f, 1f, 0f)) > 0.9f) Vec3(1f, 0f, 0f) else Vec3(0f, 1f, 0f)
        return (pick - normal * (pick dot normal)).normalized()
    }

    private fun inradiusOf(centroid: Vec3, faceVerts: List<Vec3>): Float {
        var min = Float.MAX_VALUE
        for (i in faceVerts.indices) {
            val a = faceVerts[i]
            val b = faceVerts[(i + 1) % faceVerts.size]
            val edge = b - a
            val dist = ((centroid - a) cross edge).length / edge.length
            if (dist < min) min = dist
        }
        return min
    }
}
